import React, { useMemo, useState, useRef } from 'react';

import moment from 'moment';
import InfoIcon from '../../assets/icons/InfoGrey.svg';
import DollarIcon from '../../assets/icons/currency-dollar.svg';
import { addNewFee, deleteFee, editFee } from '../../api/fees';
import ConfirmModal from '../../modals/confirm_modal';
import { Calendar } from '../UI/calendar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../UI/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox-shadcn';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { ChevronDown, FileText, Loader2, MoreHorizontal, Plus, Trash2, X } from 'lucide-react';
import EstimateStatusCell from '../estimating/EstimateStatusCell';

const ESTIMATE_STATUSES = ['Not Created', 'Created', 'Exported to QB', 'Sent to Customer'];
const INVOICE_STATUSES = ['Not Created', 'Paid', 'Partial', 'Not Paid'];

type DatePopoverState = {
  rowId: number | null;
  key: 'estimateDate' | 'invoiceDate' | null;
};

const hasMarkupOrDiscount = (type: any) => type === 'markup' || type === 'discount';

const formatClientAdjustmentValue = (type: string, value: any) => {
  const parsedValue = Number(value);
  const safeValue = Number.isFinite(parsedValue) ? parsedValue : 0;
  const signedValue = type === 'discount' ? -safeValue : safeValue;
  const formattedValue = Number.isInteger(signedValue)
    ? signedValue.toString()
    : signedValue
        .toFixed(2)
        .replace(/\.0+$/, '')
        .replace(/(\.\d*?)0+$/, '$1');

  return `${formattedValue}%`;
};

const getDisplayDate = (value: any) => {
  if (!value) return 'MM/DD/YY';
  return moment(value).isValid() ? moment(value).format('MM/DD/YY') : 'MM/DD/YY';
};

const toCalendarDate = (value: any): Date | undefined => {
  if (!value) return undefined;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
};

const EstimatesMovedDialog: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onGoToEstimating?: () => void;
}> = ({ open, onOpenChange, onGoToEstimating }) => {
  const handleGoToEstimating = () => {
    onOpenChange(false);
    onGoToEstimating?.();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[460px]" showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>Estimates Have Moved</DialogTitle>
          <DialogDescription className="leading-5">
            To create an estimate, go to the{' '}
            <strong className="font-semibold text-foreground">Estimating</strong> tab and click{' '}
            <strong className="font-semibold text-foreground">+ New Estimate</strong>. You can select
            items from your project there.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            className="font-semibold"
            onClick={() => onOpenChange(false)}
          >
            Got it
          </Button>
          <Button type="button" className="font-semibold" onClick={handleGoToEstimating}>
            Go to Estimating
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const FeesTableShadcn = (props: any) => {
  const {
    data = [],
    feeTypes = [],
    loading,
    selectedFees = [],
    handleRefetch,
    handleInlineChange,
    handleInlineDropdownChange,
    handleCheckAllFeeRows,
    handleSelectFeeRows,
    handleResetCheckedFees,
    clientPriceAdjustmentType,
    clientPriceAdjustmentValue,
    clientHasPriceAdjustment,
    showClientPriceAdjustmentColumn,
    canOpenEstimate,
    onOpenEstimate,
    onOpenEstimatingTab,
  } = props;

  const [datePopover, setDatePopover] = useState<DatePopoverState>({
    rowId: null,
    key: null,
  });
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; ids: number[] }>({
    open: false,
    ids: [],
  });
  const [estimatesMovedDialogOpen, setEstimatesMovedDialogOpen] = useState(false);
  const confirmModalRef = useRef<any>(null);

  const showClientAdjustmentColumn = !!showClientPriceAdjustmentColumn;

  const clientHasAdjustment =
    clientHasPriceAdjustment &&
    hasMarkupOrDiscount(clientPriceAdjustmentType) &&
    Number.isFinite(Number(clientPriceAdjustmentValue));

  const getFeeAdjustmentDisplay = (item: any) => {
    const isLockedOrExported =
      !!item?.clientPriceAdjustmentLockedAt || item?.estimateStatus === 'Exported to QB';

    if (hasMarkupOrDiscount(item?.clientPriceAdjustmentType)) {
      return formatClientAdjustmentValue(
        item.clientPriceAdjustmentType,
        item.clientPriceAdjustmentValue
      );
    }

    if (isLockedOrExported || clientHasAdjustment) {
      return '-';
    }

    return '-';
  };

  const handleStatusColor = (key: string, value: any) => {
    let className = 'base_status_color';
    if (value) {
      className = `${key}_${String(value).split(' ').join('_').toLowerCase()}`;
    }
    return className;
  };

  const emitInlineChange = (rowId: number, key: string, value: any) => {
    handleInlineChange?.(
      {
        target: {
          name: key,
          value,
        },
      },
      rowId
    );
  };

  const handleEditFee = async (item: any, feeKey?: string, feeValue?: any) => {
    const payloadFeeValue =
      typeof feeValue === 'object' && feeValue ? new Date(feeValue) : feeValue;

    const payload = {
      accountId: props.accountId,
      clientId: props.clientId,
      row_id: item.id,
      [feeKey || '']: payloadFeeValue || null,
    };

    const res = await editFee(payload);
    if (res && !res.error) {
      if (feeKey === 'typeId') {
        handleRefetch?.();
      } else {
        handleInlineDropdownChange?.(item.id, feeKey, payloadFeeValue);
      }
    }
  };

  const handleAddNewFee = async (typeId?: number) => {
    const payload = {
      accountId: props.accountId,
      clientId: props.clientId,
      typeId: typeId || null,
    };

    const res = await addNewFee(payload);
    if (res && !res.error) {
      handleRefetch?.();
    }
  };

  const openDeleteDialog = (ids: number[]) => {
    if (!ids.length) return;
    setDeleteDialog({ open: true, ids });
  };

  const handleDeleteFees = async () => {
    if (!deleteDialog.ids.length) return;

    const res = await deleteFee({ ids: deleteDialog.ids });
    if (res) {
      setDeleteDialog({ open: false, ids: [] });
      handleRefetch?.();
      handleResetCheckedFees?.();
    }
  };

  const confirmModalProps = {
    refs: confirmModalRef,
    active: deleteDialog.open,
    title: 'Please confirm the action',
    btn_actions: {
      confirm_btn: {
        disabled: false,
        action: () => handleDeleteFees(),
        title: 'Yes',
      },
      cancel_btn: {
        disabled: false,
        action: () => setDeleteDialog({ open: false, ids: [] }),
        title: 'No',
      },
    },
  };

  const allChecked = data.length > 0 && selectedFees.length === data.length;
  const checkedState = allChecked
    ? true
    : selectedFees.length > 0
      ? 'indeterminate'
      : false;

  const selectedCount = selectedFees.length;
  const columnsCount = useMemo(() => {
    return showClientAdjustmentColumn ? 10 : 9;
  }, [showClientAdjustmentColumn]);

  const renderAddFeeMenu = (
    buttonLabel: React.ReactNode,
    className?: string,
    options?: {
      showChevron?: boolean;
      variant?: React.ComponentProps<typeof Button>['variant'];
      align?: 'start' | 'center' | 'end';
    }
  ) => {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant={options?.variant}
            className={className}
            data-qa="client-fees-table-add-fee-trigger"
          >
            {buttonLabel}
            {options?.showChevron !== false && <ChevronDown className="h-4 w-4" />}
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          side="bottom"
          align={options?.align || 'start'}
          sideOffset={6}
          avoidCollisions={true}
          collisionPadding={12}
          className="min-w-[180px] max-h-[240px]"
        >
          {feeTypes && feeTypes.length > 0 ? (
            feeTypes.map((fee: any) => (
              <DropdownMenuItem
                key={fee.id}
                onSelect={() => handleAddNewFee(fee.id)}
                data-qa={`client-fees-table-add-fee-option-${fee.id}`}
              >
                {fee.name}
              </DropdownMenuItem>
            ))
          ) : (
            <DropdownMenuItem asChild>
              <RouterLink to="/myshop" data-qa="client-fees-table-add-fee-my-shop-link">
                My Shop
              </RouterLink>
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  if (!data || data.length < 1) {
    return (
      <div className="rounded-md border border-input bg-white p-6">
        <div className="mx-auto flex max-w-[540px] flex-col items-center justify-center gap-4 py-8 text-center">
          <DollarIcon />
          <div className="text-[20px] font-medium text-foreground">No fees have been added yet</div>
          <div className="text-[14px] text-muted-foreground">
            Your fees are managed in My Shop. Select the ones you want to use in this project.
          </div>
          {renderAddFeeMenu('Add Fee', 'min-h-[40px]', { align: 'center' })}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex min-h-[220px] w-full items-center justify-center rounded-md border border-input bg-white">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        <Table
          showTopScrollbar
          wrapperClassName="max-h-[calc(100vh-320px)]"
          className={cn(
            'table-fixed border-collapse [&_th]:whitespace-nowrap',
            showClientAdjustmentColumn ? 'min-w-[1320px]' : 'min-w-[1180px]'
          )}
        >
          <TableHeader>
            <TableRow>
              <TableHead className="w-[44px] px-[12px]" data-qa="client-fees-table-header-select">
                <div className="flex items-center justify-center">
                  <Checkbox
                    checked={checkedState as any}
                    onCheckedChange={handleCheckAllFeeRows}
                    data-qa="client-fees-table-select-all"
                  />
                </div>
              </TableHead>

              <TableHead className="w-[180px] px-[12px]" data-qa="client-fees-table-header-fee-type">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[12px] font-semibold text-[#475569]">
                    {selectedCount > 1 ? `${selectedCount} selected` : 'Fee Type'}
                  </span>

                  {selectedCount > 1 && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 gap-1 px-2 text-[12px]"
                          data-qa="client-fees-table-batch-actions-trigger"
                        >
                          Batch Actions
                          <ChevronDown className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>

                      <DropdownMenuContent align="end" className="min-w-[200px]">
                        <DropdownMenuItem
                          onSelect={(event) => {
                            event.preventDefault();
                            setEstimatesMovedDialogOpen(true);
                          }}
                          data-qa="client-fees-table-batch-create-estimate"
                          className="text-[#8B98AB] focus:!text-black data-[highlighted]:!text-black"
                        >
                          <FileText className="h-[18px] w-[18px] shrink-0 text-current" />
                          <span>Create Estimate</span>
                          <InfoIcon width={18} height={18} style={{ marginLeft: 'auto' }} />
                        </DropdownMenuItem>

                        <DropdownMenuSeparator />

                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onSelect={() => openDeleteDialog([...selectedFees])}
                          data-qa="client-fees-table-batch-delete-selected"
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete Selected
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </TableHead>

              <TableHead className="w-[340px] px-[12px]" data-qa="client-fees-table-header-description">
                Description
              </TableHead>
              <TableHead className="w-[170px] px-[12px] text-right" data-qa="client-fees-table-header-estimate">
                Estimate
              </TableHead>
              <TableHead className="w-[170px] px-[12px] text-right" data-qa="client-fees-table-header-invoice">
                Invoice
              </TableHead>
              <TableHead className="w-[80px] px-[12px] text-center" data-qa="client-fees-table-header-quantity">
                Qty.
              </TableHead>
              <TableHead className="w-[140px] px-[12px] text-right" data-qa="client-fees-table-header-unit-price">
                Unit Price
              </TableHead>

              {showClientAdjustmentColumn && (
                <TableHead
                  className="w-[130px] px-[12px] !whitespace-normal text-right leading-tight"
                  data-qa="client-fees-table-header-client-price-adjustment"
                >
                  Client Price Adjustment
                </TableHead>
              )}

              <TableHead className="w-[120px] px-[12px] text-right" data-qa="client-fees-table-header-total-price">
                Total Price
              </TableHead>
              <TableHead className="w-[52px] px-[12px] text-center" data-qa="client-fees-table-header-actions" />
            </TableRow>
          </TableHeader>

          <TableBody>
            {data.map((item: any) => {
              const isSelected = selectedFees.includes(item.id);
              const estimateDateOpen =
                datePopover.rowId === item.id && datePopover.key === 'estimateDate';
              const invoiceDateOpen = datePopover.rowId === item.id && datePopover.key === 'invoiceDate';
              const hasEstimateConnection = Boolean(
                item?.estimateId || item?.quipaEstimateId || item?.qbEstimateId
              );

              return (
                <TableRow
                  key={item.id}
                  data-qa={`client-fees-table-row-${item.id}`}
                  className={cn(
                    isSelected && 'bg-[#F1FAFA] hover:bg-[#EAF8FA]'
                  )}
                >
                  <TableCell className="px-[12px]">
                    <div className="flex items-center justify-center">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => handleSelectFeeRows(item.id)}
                        data-qa={`client-fees-table-row-select-${item.id}`}
                      />
                    </div>
                  </TableCell>

                  <TableCell className="px-[12px]">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="outline"
                          className="h-9 w-full justify-between px-3 text-left text-[13px] font-normal"
                          data-qa={`client-fees-table-row-fee-type-trigger-${item.id}`}
                        >
                          <span className={cn(!item?.item?.name && 'text-muted-foreground')}>
                            {item?.item?.name || 'Select'}
                          </span>
                          <ChevronDown className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>

                      <DropdownMenuContent align="start" className="min-w-[180px]">
                        {feeTypes && feeTypes.length > 0 ? (
                          feeTypes.map((fee: any) => (
                            <DropdownMenuItem
                              key={`${item.id}-${fee.id}`}
                              onSelect={() => handleEditFee(item, 'typeId', fee.id)}
                              data-qa={`client-fees-table-row-fee-type-option-${item.id}-${fee.id}`}
                            >
                              {fee.name}
                            </DropdownMenuItem>
                          ))
                        ) : (
                          <DropdownMenuItem disabled>No Fees</DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>

                  <TableCell className="px-[12px]">
                    <Input
                      name="feeDescription"
                      value={item.feeDescription || ''}
                      placeholder="Description"
                      onChange={(event) => emitInlineChange(item.id, 'feeDescription', event.target.value)}
                      onBlur={() => handleEditFee(item, 'feeDescription', item.feeDescription)}
                      className="h-9 text-[13px]"
                      data-qa={`client-fees-table-row-description-${item.id}`}
                    />
                  </TableCell>

                  <TableCell className="px-[12px] text-right align-top">
                    <div className="space-y-1">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            className="inline-flex w-full items-center justify-end gap-1 text-right text-[13px]"
                            data-qa={`client-fees-table-row-estimate-status-trigger-${item.id}`}
                          >
                            <span className={handleStatusColor('estimateStatus', item.estimateStatus)}>
                              {item.estimateStatus || 'select'}
                            </span>
                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                          </button>
                        </DropdownMenuTrigger>

                        <DropdownMenuContent align="end" className="min-w-[170px]">
                          {ESTIMATE_STATUSES.map((status) => (
                            <DropdownMenuItem
                              key={`${item.id}-estimate-status-${status}`}
                              onSelect={() => handleEditFee(item, 'estimateStatus', status)}
                              data-qa={`client-fees-table-row-estimate-status-option-${item.id}-${status
                                .toLowerCase()
                                .replace(/\s+/g, '-')}`}
                            >
                              <span className={handleStatusColor('estimateStatus', status)}>{status}</span>
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>

                      {hasEstimateConnection ? (
                        <EstimateStatusCell
                          summary={item.estimateStatusSummary}
                          status={item.estimateStatus || 'Not Created'}
                          date={item.estimateDate}
                          estimateId={item.estimateId}
                          estimateNumber={item.estimateNumber}
                          qbEstimateId={item.qbEstimateId}
                          qbEstimateNumber={item.qbEstimateNumber}
                          canOpenEstimate={!!canOpenEstimate && !!onOpenEstimate}
                          onOpenEstimate={onOpenEstimate}
                          getStatusClassName={(status) => handleStatusColor('estimateStatus', status)}
                          align="right"
                          compact
                          showStatusLine={false}
                          dataQaPrefix={`client-fees-table-row-${item.id}`}
                        />
                      ) : (
                        <Input
                          name="estimateNumber"
                          value={item.estimateNumber || ''}
                          placeholder="#_"
                          onChange={(event) =>
                            emitInlineChange(item.id, 'estimateNumber', event.target.value)
                          }
                          onBlur={(event) =>
                            handleEditFee(item, 'estimateNumber', event.currentTarget.value)
                          }
                          className="ml-auto h-5 min-h-0 max-w-[120px] border-0 bg-transparent p-0 text-right text-[12px] shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                          data-qa={`client-fees-table-row-estimate-number-${item.id}`}
                        />
                      )}

                      <div className="group/date flex items-center justify-end gap-1 text-[12px]">
                        <Popover
                          open={estimateDateOpen}
                          onOpenChange={(open) => {
                            setDatePopover(open ? { rowId: item.id, key: 'estimateDate' } : { rowId: null, key: null });
                          }}
                        >
                          <PopoverTrigger asChild>
                            <button
                              type="button"
                              className="rounded px-1 py-0.5 text-right text-[12px] text-foreground hover:bg-muted"
                              data-qa={`client-fees-table-row-estimate-date-${item.id}`}
                            >
                              {getDisplayDate(item.estimateDate)}
                            </button>
                          </PopoverTrigger>

                          <PopoverContent className="w-auto p-0" align="end" onOpenAutoFocus={(e) => e.preventDefault()}>
                            <Calendar
                              mode="single"
                              selected={toCalendarDate(item.estimateDate)}
                              onSelect={(value) => {
                                handleInlineDropdownChange?.(item.id, 'estimateDate', value || null);
                                handleEditFee(item, 'estimateDate', value || null);
                                setDatePopover({ rowId: null, key: null });
                              }}
                              className="rounded-md border-0 p-[12px]"
                            />
                            {item.estimateDate && (
                              <div className="border-t px-3 py-2">
                                <button
                                  type="button"
                                  className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-destructive"
                                  onClick={() => {
                                    handleInlineDropdownChange?.(item.id, 'estimateDate', null);
                                    handleEditFee(item, 'estimateDate', null);
                                    setDatePopover({ rowId: null, key: null });
                                  }}
                                >
                                  <X className="h-3 w-3" />
                                  Clear
                                </button>
                              </div>
                            )}
                          </PopoverContent>
                        </Popover>
                        {item.estimateDate && (
                          <button
                            type="button"
                            aria-label="Clear estimate date"
                            data-qa={`client-fees-table-row-estimate-date-clear-${item.id}`}
                            className="h-3.5 w-3.5 rounded text-muted-foreground opacity-60 transition-opacity group-hover/date:opacity-100 hover:bg-muted hover:text-foreground hover:[&_svg]:text-destructive"
                            onMouseDown={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              handleInlineDropdownChange?.(item.id, 'estimateDate', null);
                              handleEditFee(item, 'estimateDate', null);
                              setDatePopover({ rowId: null, key: null });
                            }}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  </TableCell>

                  <TableCell className="px-[12px] text-right align-top">
                    <div className="space-y-1">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            className="inline-flex w-full items-center justify-end gap-1 text-right text-[13px]"
                            data-qa={`client-fees-table-row-invoice-status-trigger-${item.id}`}
                          >
                            <span className={handleStatusColor('invoicePaid', item.invoiceStatus)}>
                              {item.invoiceStatus || 'select'}
                            </span>
                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                          </button>
                        </DropdownMenuTrigger>

                        <DropdownMenuContent align="end" className="min-w-[170px]">
                          {INVOICE_STATUSES.map((status) => (
                            <DropdownMenuItem
                              key={`${item.id}-invoice-status-${status}`}
                              onSelect={() => handleEditFee(item, 'invoiceStatus', status)}
                              data-qa={`client-fees-table-row-invoice-status-option-${item.id}-${status
                                .toLowerCase()
                                .replace(/\s+/g, '-')}`}
                            >
                              <span className={handleStatusColor('invoicePaid', status)}>{status}</span>
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>

                      <div className="flex items-center justify-end gap-2 text-[12px]">
                        <Popover
                          open={invoiceDateOpen}
                          onOpenChange={(open) => {
                            setDatePopover(open ? { rowId: item.id, key: 'invoiceDate' } : { rowId: null, key: null });
                          }}
                        >
                          <PopoverTrigger asChild>
                            <button
                              type="button"
                              className="rounded px-1 py-0.5 text-right text-[12px] text-foreground hover:bg-muted"
                              data-qa={`client-fees-table-row-invoice-date-${item.id}`}
                            >
                              {getDisplayDate(item.invoiceDate)}
                            </button>
                          </PopoverTrigger>

                          <PopoverContent className="w-auto p-0" align="end" onOpenAutoFocus={(e) => e.preventDefault()}>
                            <Calendar
                              mode="single"
                              selected={toCalendarDate(item.invoiceDate)}
                              onSelect={(value) => {
                                handleInlineDropdownChange?.(item.id, 'invoiceDate', value || null);
                                handleEditFee(item, 'invoiceDate', value || null);
                                setDatePopover({ rowId: null, key: null });
                              }}
                              className="rounded-md border-0 p-[12px]"
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>
                  </TableCell>

                  <TableCell className="px-[12px]">
                    <Input
                      name="quantity"
                      value={item.quantity || ''}
                      onChange={(event) => emitInlineChange(item.id, 'quantity', event.target.value)}
                      onBlur={() => handleEditFee(item, 'quantity', item.quantity)}
                      className="h-9 text-center text-[13px]"
                      data-qa={`client-fees-table-row-quantity-${item.id}`}
                    />
                  </TableCell>

                  <TableCell className="px-[12px]">
                    <Input
                      name="unitPrice"
                      value={item.unitPrice && item.unitPrice !== '0.00' ? `$${item.unitPrice}` : ''}
                      placeholder="$0.00"
                      onChange={(event) => emitInlineChange(item.id, 'unitPrice', event.target.value)}
                      onBlur={() => handleEditFee(item, 'unitPrice', item.unitPrice)}
                      className="h-9 text-right text-[13px]"
                      data-qa={`client-fees-table-row-unit-price-${item.id}`}
                      style={{
                        color: Number(item.unitPrice) ? 'inherit' : '#9c9c9c',
                      }}
                    />
                  </TableCell>

                  {showClientAdjustmentColumn && (
                    <TableCell className="px-[12px] text-right">
                      <span
                        className="text-[13px]"
                        data-qa={`client-fees-table-row-client-price-adjustment-${item.id}`}
                      >
                        {getFeeAdjustmentDisplay(item)}
                      </span>
                    </TableCell>
                  )}

                  <TableCell className="px-[12px] text-right">
                    <span
                      className="text-[13px] font-semibold"
                      data-qa={`client-fees-table-row-total-price-${item.id}`}
                      style={{
                        color: item.quantity && Number(item.unitPrice) ? 'inherit' : '#9c9c9c',
                      }}
                    >
                      ${item.quantity && Number(item.unitPrice) ? item.totalPrice : 'TBD'}
                    </span>
                  </TableCell>

                  <TableCell className="px-[12px]">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          data-qa={`client-fees-table-row-actions-trigger-${item.id}`}
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>

                      <DropdownMenuContent align="end" className="min-w-[190px]">
                        <DropdownMenuItem
                          onSelect={(event) => {
                            event.preventDefault();
                            setEstimatesMovedDialogOpen(true);
                          }}
                          data-qa={`client-fees-table-row-create-estimate-${item.id}`}
                          className="text-[#8B98AB] focus:!text-black data-[highlighted]:!text-black"
                        >
                          <FileText className="h-[18px] w-[18px] shrink-0 text-current" />
                          <span>Create Estimate</span>
                          <InfoIcon width={18} height={18} style={{ marginLeft: 'auto' }} />
                        </DropdownMenuItem>

                        <DropdownMenuSeparator />

                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onSelect={() => openDeleteDialog([item.id])}
                          data-qa={`client-fees-table-row-delete-${item.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              );
            })}

            {data.length === 0 && (
              <TableRow>
                <TableCell colSpan={columnsCount} className="px-[12px] py-8 text-center text-muted-foreground">
                  No fees found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <div className="flex items-center justify-start">
          {renderAddFeeMenu(
            <span className="inline-flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Add Fee
            </span>,
            '!min-h-0 h-auto rounded-none border-0 bg-transparent p-0 text-[#3B97B1] shadow-none hover:bg-transparent hover:text-[#2f8196]',
            { showChevron: false, variant: 'ghost' }
          )}
        </div>
      </div>

      <ConfirmModal {...confirmModalProps}>
        <span>
          Are you sure you want to delete {deleteDialog.ids.length}{' '}
          {deleteDialog.ids.length <= 1 ? 'fee' : 'fees'}?
        </span>
      </ConfirmModal>

      <EstimatesMovedDialog
        open={estimatesMovedDialogOpen}
        onOpenChange={setEstimatesMovedDialogOpen}
        onGoToEstimating={onOpenEstimatingTab}
      />
    </>
  );
};

export default FeesTableShadcn;
